import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import SetupWizard from "./pages/SetupWizard";
import AppLayout from "./components/layout/AppLayout";
import DashboardPage from "./pages/DashboardPage";
import CallsPage from "./pages/CallsPage";
import CallDetailPage from "./pages/CallDetailPage";
import OrdersPage from "./pages/OrdersPage";
import KnowledgePage from "./pages/KnowledgePage";
import LiveCallsPage from "./pages/LiveCallsPage";
import OpsPage from "./pages/OpsPage";
import OpsUserDetailPage from "./pages/OpsUserDetailPage";
import OpsLoginPage from "./pages/OpsLoginPage";
import OpsRestaurantPage from "./pages/OpsRestaurantPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import FAQsPage from "./pages/FAQsPage";
import MenuPage from "./pages/MenuPage";
import SettingsPage from "./pages/SettingsPage";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function SetupGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.setup_complete) return <Navigate to="/setup" replace />;
  return children;
}

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (user) {
    return <Navigate to={user.setup_complete ? "/dashboard" : "/setup"} replace />;
  }
  return children;
}

function LandingRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (user) return <Navigate to={user.setup_complete ? "/dashboard" : "/setup"} replace />;
  return <LandingPage />;
}

function OpsGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-900"><Loader2 className="w-8 h-8 animate-spin text-red-400" /></div>;
  if (!user) return <Navigate to="/ops/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/ops/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing page */}
      <Route path="/" element={<LandingRedirect />} />

      {/* Public routes */}
      <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
      <Route path="/register" element={<AuthRedirect><RegisterPage /></AuthRedirect>} />

      {/* Setup wizard */}
      <Route path="/setup" element={<ProtectedRoute><SetupWizard /></ProtectedRoute>} />

      {/* App routes */}
      <Route element={<SetupGuard><AppLayout /></SetupGuard>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/calls" element={<CallsPage />} />
        <Route path="/calls/live" element={<LiveCallsPage />} />
        <Route path="/calls/:id" element={<CallDetailPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Public pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/faqs" element={<FAQsPage />} />

      {/* Ops/Admin routes — own login flow */}
      <Route path="/ops/login" element={<OpsLoginPage />} />
      <Route path="/ops" element={<OpsGuard><OpsPage /></OpsGuard>} />
      <Route path="/ops/users/:userId" element={<OpsGuard><OpsUserDetailPage /></OpsGuard>} />
      <Route path="/ops/users/:userId/restaurant" element={<OpsGuard><OpsRestaurantPage /></OpsGuard>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
